import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConfidenceBadge } from "@/components/Badges";
import { ChevronLeft, ChevronRight, Filter, X } from "lucide-react";

const TIERS = ["", "reviewed", "confirmed", "high", "review", "candidate"];
const HAS_LABELS = { it: "80G / 12A (Income Tax)", fcra: "FCRA registered", csr1: "CSR-1 registered", donors: "Donor info available", guidestar: "GuideStar certified" };

export default function Mapping() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [tier, setTier] = useState("");
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const has = searchParams.get("has") || "";
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const res = await api.get("/v1/mapping", { params: { page, page_size: 25, tier, q, has } });
    setData(res.data);
  }, [page, tier, q, has]);

  useEffect(() => { setPage(1); }, [has]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div>
      <PageHeader
        eyebrow="ngo_entity_map"
        title="Entity Crosswalk Database"
        subtitle="Every NGO appearing in two or more registries, with the confidence of each link."
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="mapping-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Filter by name or PAN…"
            className="w-full rounded-full border border-slate-700 bg-slate-950/70 pl-10 pr-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors duration-150"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TIERS.map((t) => (
            <button
              key={t || "all"}
              data-testid={`mapping-tier-filter-${t || "all"}`}
              onClick={() => { setTier(t); setPage(1); }}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition-colors duration-150 ${
                tier === t ? "border-blue-500 bg-blue-500/15 text-blue-300" : "border-slate-700 text-slate-400 hover:text-slate-200"
              }`}
            >
              {t || "all"}
            </button>
          ))}
        </div>
      </div>

      {has && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-700/50 bg-blue-500/10 px-3 py-1.5 text-xs font-medium text-blue-300" data-testid="mapping-has-filter">
          Showing only: {HAS_LABELS[has] || has}
          <button onClick={() => setSearchParams({})} data-testid="mapping-has-clear" className="ml-1 rounded-full p-0.5 hover:bg-blue-500/20">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 sticky top-0">
            <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">NGO</th>
              <th className="px-4 py-3">PAN</th>
              <th className="px-4 py-3">Darpan</th>
              <th className="px-4 py-3">FCRA</th>
              <th className="px-4 py-3">CSR-1</th>
              <th className="px-4 py-3">Overall</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => (
              <tr
                key={r.entity_key}
                data-testid={`mapping-row-${r.entity_key}`}
                onClick={() => navigate(`/app/ngo/${encodeURIComponent(r.entity_key)}`)}
                className="border-t border-slate-800/60 hover:bg-slate-900/60 cursor-pointer transition-colors duration-100"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-200 max-w-[220px] truncate">{r.display_name}</div>
                  <div className="text-[11px] text-slate-500">{r.state}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-sky-700">{r.pan || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-purple-700">{r.darpan_ids?.[0] || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-amber-700">{r.fcra_reg_no || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-emerald-700">{r.csr1 || "—"}</td>
                <td className="px-4 py-3">{r.overall_tier_info && <ConfidenceBadge info={r.overall_tier_info} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="font-mono text-xs text-slate-500">
          {data?.total?.toLocaleString()} entities · page {page} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="mapping-prev-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 disabled:opacity-40 hover:border-slate-500 transition-colors duration-150"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button
            data-testid="mapping-next-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 disabled:opacity-40 hover:border-slate-500 transition-colors duration-150"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
